import axios from 'axios';
import baseApi from './base';
import { subDays, startOfDay, isSameDay, isBefore, parseISO } from 'date-fns';
import logComponent from '../logComponent';

const convenienceApi = (function() {
  
  /* ====================
  === PRIVATE METHODS ===
  ==================== */
  function getUpdatedCorporationFields(stagedCorp, mimiCorp) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getUpdatedCorporationFields'); }

    let updatedFields = [];
  
    if (stagedCorp.name !== mimiCorp.name) updatedFields.push('name');
    if (stagedCorp.postalCode !== mimiCorp.postalCode) updatedFields.push('postalCode');
    if (stagedCorp.aboutUs !== mimiCorp.aboutUs) updatedFields.push('aboutUs');
    if (stagedCorp.industryType !== mimiCorp.industryType) updatedFields.push('industryType');
    if (stagedCorp.salesforceAccountId !== mimiCorp.salesforceAccountId) updatedFields.push('salesforceAccountId');
  
    if (linkedFacilitiesAreDifferent(stagedCorp.uiData.linkedFacilityIds, mimiCorp.uiData.linkedFacilityIds)) updatedFields.push('linkedFacilities');
    
    return updatedFields;
  }
  
  function getUpdatedFacilityFields(stagedFacility, mimiFacility) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getUpdatedFacilityFields'); }
    let updatedFields = [];
  
    if (stagedFacility.name !== mimiFacility.name) updatedFields.push('name');
    if (stagedFacility.aboutUs !== mimiFacility.aboutUs) updatedFields.push('aboutUs');
    if (stagedFacility.postalCode !== mimiFacility.postalCode) updatedFields.push('postalCode');
    if (stagedFacility.industryType !== mimiFacility.industryType) updatedFields.push('industryType');
    if (stagedFacility.usingSara !== mimiFacility.usingSara) updatedFields.push('usingSara');
    if (stagedFacility.usingCatie !== mimiFacility.usingCatie) updatedFields.push('usingCatie');
    if (stagedFacility.usingEMessenger !== mimiFacility.usingEMessenger) updatedFields.push('usingEMessenger');  
    if (stagedFacility.salesforceSiteId !== mimiFacility.salesforceSiteId) updatedFields.push('salesforceSiteId');  
    if (stagedFacility.salesforceAccountId !== mimiFacility.salesforceAccountId) updatedFields.push('salesforceAccountId');  
    
    if (linkedCorporationsAreDifferent(stagedFacility.uiData.linkedCorporationIds, mimiFacility.uiData.linkedCorporationIds)) updatedFields.push('linkedCorporations');
  
    return updatedFields;
  }
  
  function linkedFacilitiesAreDifferent(facilities1, facilities2) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi linkedFacilitiesAreDifferent'); }
    if (facilities1.length !== facilities2.length) return true;
    if (facilities1.some(facility1 => !facilities2.some(facility2 => facility1 === facility2))) return true;
    if (facilities2.some(facility2 => !facilities1.some(facility1 => facility2 === facility1))) return true; 
  
    return false;
  }
  
  function linkedCorporationsAreDifferent(corps1, corps2) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi linkedCorporationsAreDifferent'); }
    if (corps1.length !== corps2.length) return true;
    if (corps1.some(corp1 => !corps2.some(corp2 => corp1 === corp2))) return true;
    if (corps2.some(corp2 => !corps1.some(corp1 => corp2 === corp1))) return true;
    
    return false;
  }  

  function facilityCanUtilizeModule(facility, module) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi facilityCanUtilizeModule'); }
    if (module.saraDependent && facility.usingSara) return true;
    if (module.catieDependent && facility.usingCatie) return true;
    if (module.eMessengerDependent && facility.usingEMessenger) return true;
    if (!module.saraDependent && !module.catieDependent && !module.eMessengerDependent) return true;
  
    return false;
  }

  /* ===================
  === PUBLIC METHODS ===
  =================== */
  function getSecuredData(force) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getSecuredData'); }
    // Fetch the user
    return baseApi.User.get()
      .then(result => {
        const user = result.data;

        // If family user, check selectedMI from localStorage
        let selectedMonitoredIndividual = null;

        if (user.groupTypeString === 'Family') {
          const previousEnvironmentContext = JSON.parse(sessionStorage.getItem('previousEnvironmentContext'));
          
          // If previousEnvironmentContext is recent (within last 15 seconds), load selectedMI from there
          if (!force && previousEnvironmentContext && 
              previousEnvironmentContext.unloadTime && 
              typeof previousEnvironmentContext.unloadTime === 'number' &&
              Date.now() < previousEnvironmentContext.unloadTime + 15000 &&
              previousEnvironmentContext.user &&
              previousEnvironmentContext.user.userId === user.userId &&
              previousEnvironmentContext.selectedMonitoredIndividual) {
                selectedMonitoredIndividual = previousEnvironmentContext.selectedMonitoredIndividual;
          } else {
            selectedMonitoredIndividual = user.monitoredIndividualUsers[0].monitoredIndividual;
          }
        }

        const selectedMonitoredIndividualId = selectedMonitoredIndividual ? selectedMonitoredIndividual.monitoredIndividualId : null;

        // Fetch user's organization and modules
        return axios.all([
          getOrganizationByUser(user, selectedMonitoredIndividual),
          baseApi.Module.GetAllForCurrentUser(selectedMonitoredIndividualId),
          baseApi.Survey.GetCheckInSurveyForCurrentUser()
        ]).then(axios.spread((organization, modulesResp, surveyResp) => {
          return {
            user,
            organization,
            selectedMonitoredIndividual,
            userModules: modulesResp.data,
            checkinSurvey: surveyResp.data
          }
        }))
      });
  }

  function getFacilityMonitoredIndividuals(facility) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getFacilityMonitoredIndividuals'); }
    const { facilityId } = facility;
    if (!facilityId) return;

    return baseApi.Facility.GetAllMonitoredIndividuals(facilityId)
      .then (result => result.data);
  }

  function getWeblinksForOrganization(organization) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getWeblinksForOrganization'); }
    if (organization.facilityId) {
      return baseApi.Facility.Id(organization.facilityId, false)
        .then(result => result.data.webLinks);
    } else if (organization.corporationId) {
      return baseApi.Corporation.Id(organization.corporationId, true)
        .then(result => result.data.webLinks);
    } else {
      return Promise.resolve([]);
    }
  }

  function getOrganizationByUser(user, selectedMonitoredIndividual) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getOrganizationByUser'); }
    if (!user.groupTypeString) return;

    switch(user.groupTypeString) {
      case 'Status Solutions':
        return Promise.resolve(null);
      case 'Corporate':
        return baseApi.Corporation.Id(user.corporationId, true)
          .then(result => result.data);
      case 'Facility':
        return baseApi.Facility.Id(user.facilityId, false) 
          .then(result => result.data);
      case 'Family': 
      
        const { monitoredIndividualId } = selectedMonitoredIndividual;
        
        if (monitoredIndividualId === null) return Promise.reject(new Error('Cannot fetch organization for Family user without selectedMonitoredIndividual'));

        return baseApi.Facility.GetByCurrentUser(monitoredIndividualId, false)
          .then(result => result.data);
      default:
        throw new Error('User GroupTypeString not recognized');
    }
  }

  function getFamilyRelationships(user) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getFamilyRelationships'); }
    return baseApi.User.GetFamilyRelationships(user.userId)
      .then(result => result.data);
  }

  function initializeUserUpdateModal(adminUser, user) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi initializeUserUpdateModal'); }
    switch (adminUser.groupTypeString) {
      case 'Family': 
        // Get user's linked monitored individuals
        return baseApi.User.GetFamilyRelationships(user.userId)
          .then(result => {
            const allMonitoredIndividualUsers = result.data;

            // Put all MIUsers connected to user but NOT managed by adminUser
            let externalMonitoredIndividualUsers = allMonitoredIndividualUsers.filter(MIUser => {
              return !adminUser.monitoredIndividualUsers.some(adminMIUser => (MIUser.monitoredIndividualId === adminMIUser.monitoredIndividualId) && adminMIUser.isAdmin);
            })
                          
            let selectedMonitoredIndividualUsers = allMonitoredIndividualUsers
              .filter(MIUser => {
                return adminUser.monitoredIndividualUsers.some(adminMIUser => adminMIUser.isAdmin && adminMIUser.monitoredIndividualId === MIUser.monitoredIndividualId);
              })
              .map(MIUser => {
                MIUser.selected = true;
                return MIUser;
              })

              let potentialMonitoredIndividualUsers = 
              adminUser.monitoredIndividualUsers
                .filter(MIUser => MIUser.isAdmin)
                .map(MIUser => {
                  let selectedMIUser = selectedMonitoredIndividualUsers.find(selectedMIUser => selectedMIUser.monitoredIndividualId === MIUser.monitoredIndividualId);

                  if (selectedMIUser) {
                    selectedMIUser.monitoredIndividual.monitoredIndividualUnits = MIUser.monitoredIndividual.monitoredIndividualUnits;
                    return selectedMIUser;
                  }

                  let newMIUser = Object.assign({}, MIUser);
                  newMIUser.isAdmin = null;
                  newMIUser.userId = user.userId;
                  newMIUser.selected = false;

                  return newMIUser;
              });

            return {
              externalMonitoredIndividualUsers,
              potentialMonitoredIndividualUsers,
              selectedMonitoredIndividualUsers
            };
          });
      case 'Facility':
        if (user.groupTypeString === 'Family') {
          return axios.all([
            baseApi.User.GetFamilyRelationships(user.userId),
            baseApi.Facility.GetAllMonitoredIndividuals(adminUser.facilityId)

          ])
            .then(axios.spread((selectedMonitoredIndividualsResp, potentialMonitoredIndividualsResp) => {
              // pull data from responses
              let selectedMonitoredIndividualUsers = selectedMonitoredIndividualsResp.data;
              let potentialMonitoredIndividuals = potentialMonitoredIndividualsResp.data;

              selectedMonitoredIndividualUsers = selectedMonitoredIndividualUsers.map(MIUser => {
                MIUser.selected = true;
                return MIUser;
              })

              let potentialMonitoredIndividualUsers = potentialMonitoredIndividuals.map(monitoredIndividual => {
                let selectedMIUser = selectedMonitoredIndividualUsers.find(selectedMIUser => selectedMIUser.monitoredIndividualId === monitoredIndividual.monitoredIndividualId);

                if (selectedMIUser) {
                  selectedMIUser.monitoredIndividual.monitoredIndividualUnits = monitoredIndividual.monitoredIndividualUnits;
                  return selectedMIUser;
                }
            
                let monitoredIndividualUser = {};
                monitoredIndividualUser.monitoredIndividualId = monitoredIndividual.monitoredIndividualId;
                monitoredIndividualUser.monitoredIndividual = monitoredIndividual;
                monitoredIndividualUser.userId = user.userId;
                monitoredIndividualUser.isAdmin = null;
                monitoredIndividualUser.selected = false;

                return monitoredIndividualUser;
              });

              return {
                selectedMonitoredIndividualUsers,
                potentialMonitoredIndividualUsers
              };
            }));    
        } else if (user.groupTypeString === 'Facility') {
          return axios.all([
            baseApi.User.GetGroups(user.userId),
            baseApi.Facility.GetAllGroups(adminUser.facilityId),
            baseApi.User.GetUserCheckInSurvey(user.userId),
            baseApi.Survey.GetAllCheckInSurveysForFacility(adminUser.facilityId)
          ])
            .then(axios.spread((userGroupsResp, facilityGroupsResp, checkInSurveyResponse, allCheckInSurveysResponse) => {
              let userGroups = userGroupsResp.data;
              let facilityGroups = facilityGroupsResp.data;
              let checkInSurvey = checkInSurveyResponse.data;
              let facilityCheckInSurveys = allCheckInSurveysResponse.data;

              return {
                potentialGroups: facilityGroups,
                selectedGroupIds: userGroups.map(userGroup => userGroup.groupId),
                potentialSurveys: facilityCheckInSurveys,
                checkInSurveyId: checkInSurvey?.surveyId
              }
            }));
        }
        break;
      case 'Corporate':
        return axios.all([
            baseApi.User.GetGroups(user.userId),
            baseApi.Corporation.GetAllGroups(adminUser.corporationId)
          ])
          .then(axios.spread((userGroupsResp, corporationGroupsResp) => {
            let userGroups = userGroupsResp.data;
            let corporationGroups = corporationGroupsResp.data;


            return {
              potentialGroups: corporationGroups,
              selectedGroupIds: userGroups.map(userGroup => userGroup.groupId)
            }
          }));
      case 'Status Solutions':
        switch (user.groupTypeString) {
          case 'Family':
            return baseApi.User.GetFamilyRelationships(user.userId)
              .then(
                result => {
                  let selectedMonitoredIndividualUsers = result.data;

                  if (selectedMonitoredIndividualUsers.length === 0) {
                    return {
                      selectedFacilities: [], 
                      selectedMonitoredIndividualUsers: [], 
                      potentialMonitoredIndividualUsers: [] 
                    }
                  } else {
                    return axios.all([
                      baseApi.Facility.GetByUser(user.userId, selectedMonitoredIndividualUsers[0].monitoredIndividualId),
                      baseApi.Facility.GetAllMonitoredIndividuals(selectedMonitoredIndividualUsers[0].monitoredIndividual.lastKnownFacilityId)
                    ])
                      .then(axios.spread((facilityResp, monitoredIndividualsResp) => {
                        const selectedFacility = facilityResp.data
                        const potentialMonitoredIndividuals = monitoredIndividualsResp.data;

                        selectedMonitoredIndividualUsers = selectedMonitoredIndividualUsers.map(MIUser => {
                          MIUser.selected = true;
                          return MIUser;
                        });

                        let potentialMonitoredIndividualUsers = potentialMonitoredIndividuals.map(monitoredIndividual => {
                          let selectedMIUser = selectedMonitoredIndividualUsers.find(selectedMIUser => selectedMIUser.monitoredIndividualId === monitoredIndividual.monitoredIndividualId);
  
                          if (selectedMIUser) {
                            selectedMIUser.monitoredIndividual.monitoredIndividualUnits = monitoredIndividual.monitoredIndividualUnits;
                            return selectedMIUser;
                          }
  
                          let monitoredIndividualUser = {};
                          monitoredIndividualUser.monitoredIndividualId = monitoredIndividual.monitoredIndividualId;
                          monitoredIndividualUser.monitoredIndividual = monitoredIndividual;
                          monitoredIndividualUser.userId = user.userId;
                          monitoredIndividualUser.isAdmin = null; 
                          monitoredIndividualUser.selected = false; 
        
                          return monitoredIndividualUser;
                        });
        
                        return { 
                          selectedFacilities: [selectedFacility], 
                          selectedMonitoredIndividualUsers, 
                          potentialMonitoredIndividualUsers 
                        };
                      }));
                  }
                }
              );
          case 'Facility':
            return axios.all([
              baseApi.Facility.GetByUser(user.userId),
              baseApi.User.GetGroups(user.userId),
              baseApi.Facility.GetAllGroups(user.facilityId),
              baseApi.User.GetUserCheckInSurvey(user.userId),
              baseApi.Survey.GetAllCheckInSurveysForFacility(user.facilityId)
            ])
              .then(axios.spread((selectedFacilityResp, selectedGroupsResp, potentialGroupsResp, checkInSurveyResponse, allCheckInSurveysResponse) => {
                // Pull data from responses
                let selectedFacility = selectedFacilityResp.data;
                let selectedGroups = selectedGroupsResp.data;
                let potentialGroups = potentialGroupsResp.data;
                let checkInSurvey = checkInSurveyResponse.data;
                let facilityCheckInSurveys = allCheckInSurveysResponse.data;
     
                return {
                  selectedFacilities: [selectedFacility],
                  selectedGroupIds: selectedGroups.map(selectedGroup => selectedGroup.groupId),
                  potentialGroups,
                  potentialSurveys: facilityCheckInSurveys,
                  checkInSurveyId: checkInSurvey?.surveyId
                }
              }));
          case 'Corporate':
            return axios.all([
                baseApi.Corporation.Id(user.corporationId),
                baseApi.User.GetGroups(user.userId),
                baseApi.Corporation.GetAllGroups(user.corporationId)
              ])
              .then(axios.spread((selectedCorporationResp, selectedGroupsResp, potentialGroupsResp) => {
                // Pull data from responses
                let selectedCorporation = selectedCorporationResp.data;
                let selectedGroups = selectedGroupsResp.data;
                let potentialGroups = potentialGroupsResp.data;

                return {
                  selectedCorporations: [selectedCorporation],
                  selectedGroupIds: selectedGroups.map(selectedGroup => selectedGroup.groupId),
                  potentialGroups
                }
              }));
          case 'Status Solutions':
            return axios.all([
                baseApi.User.GetGroups(user.userId),
                baseApi.StatusSolutions.GetAllGroups(adminUser.statusSolutionsId)
              ])
              .then(axios.spread((userGroupsResp, statusSolutionsGroupsResp) => {
                let userGroups = userGroupsResp.data;
                let statusSolutionsGroups = statusSolutionsGroupsResp.data;


                return {
                  potentialGroups: statusSolutionsGroups,
                  selectedGroupIds: userGroups.map(userGroup => userGroup.groupId)
                }
              }));
          default:
            return Promise.reject(new Error('User GroupType not recognized'));  
        }
      default:
        return Promise.reject(new Error('Admin User GroupType not recognized'));
    }
  }

  function getMonitoredIndividualUsersFromMultipleFacilities(facilityIds, user) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getMonitoredIndividualUsersFromMultipleFacilities'); }
    let requests = [];

    facilityIds.forEach((facilityId) => {
      requests.push(baseApi.Facility.GetAllMonitoredIndividuals.bind(null, facilityId));
    });

    return axios.all(requests.map(request => request()))
      .then(axios.spread((...responses) => {
        return responses.reduce((prev, curr) => prev.concat(curr.data), []);
      }))
  }

  function updateUser(prevUser, newUser) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi updateUser'); }
    let addedGroups = []
    let removedGroups = [];
    let requests = [];
    let subRequests = [];
    let addedUserSurveyTypes = []
    let removedUserSurveyTypes = [];

    // If user has groups, check for updates
    // NOTE: newUser.Group is an array of Group OBJECTS
    // prevUser.Group is an array of Group IDs
    if (newUser.groups && prevUser.groups) {
      addedGroups = newUser.groups
        .filter(newGroup => !prevUser.groups.some(prevGroup => newGroup.groupId === prevGroup))
        .map(newGroup => newGroup.groupId);
      removedGroups = prevUser.groups.filter(prevGroup => !newUser.groups.some(newGroup => newGroup.groupId === prevGroup));
    }


    if (newUser.checkInSurveyId && !prevUser.checkInSurveyId && newUser.checkInSurveyId !== "") {
      let surveyTypeSurveyAdd = { surveyTypeId: 1, surveyId: newUser.checkInSurveyId };
      addedUserSurveyTypes.push(surveyTypeSurveyAdd);
      console.log('convenienceApi updateUser - adding surveyTypeId = 1, surveyId = ' + newUser.checkInSurveyId.toString());
    }



    if (!newUser.checkInSurveyId && prevUser.checkInSurveyId) {
      let surveyTypeSurveyRemove = { surveyTypeId: 1, surveyId: prevUser.checkInSurveyId };
      removedUserSurveyTypes.push(surveyTypeSurveyRemove);
      console.log('convenienceApi updateUser - removing surveyTypeId = 1, surveyId = ' + prevUser.checkInSurveyId.toString());
    }


    if (newUser.checkInSurveyId && prevUser.checkInSurveyId && newUser.checkInSurveyId !== prevUser.checkInSurveyId && newUser.checkInSurveyId !== "") {
      let surveyTypeSurveyAdd = { surveyTypeId: 1, surveyId: newUser.checkInSurveyId };
      let surveyTypeSurveyRemove = { surveyTypeId: 1, surveyId: prevUser.checkInSurveyId };

      console.log('convenienceApi updateUser - adding surveyTypeId = 1, surveyId = ' + newUser.checkInSurveyId.toString());
      console.log('convenienceApi updateUser - removing surveyTypeId = 1, surveyId = ' + prevUser.checkInSurveyId.toString());
      removedUserSurveyTypes.push(surveyTypeSurveyRemove);
      addedUserSurveyTypes.push(surveyTypeSurveyAdd);
    }


    // If user's facility id is being updated, run user update 
    // and group assignment calls in series
    if (prevUser.facilityId && newUser.facilityId && prevUser.facilityId !== newUser.facilityId) {
      return baseApi.User.AddOrUpdate(newUser)
        .then(result => {
          // run group addition requests only; TODO: handle errors here
          baseApi.User.AddGroups(newUser.userId, addedGroups);
          baseApi.User.RemoveUserSurveyTypes(newUser.userId, removedUserSurveyTypes)
            .then(() => baseApi.User.AddUserSurveyTypes(newUser.userId, addedUserSurveyTypes));

          return result.data;
        }).catch(error => {
          throw new Error('User not updated');
        });

    }


    // If user's corporation id is being updated, run user update 
    // and group assignment calls in series
    if (prevUser.corporationId && newUser.corporationId && prevUser.corporationId !== newUser.corporationId) {
      return baseApi.User.AddOrUpdate(newUser)
        .then(result => {
          // run group addition requests only; TODO: handle errors here
          baseApi.User.AddGroups(newUser.userId, addedGroups);

          return result.data;
        })
    }

    // Otherwise, check whether non-group user properties have been updated
    let userUpdated = false;

    for (let key in newUser) {
      if (key !== 'groups' && prevUser[key] !== newUser[key]) {
        userUpdated = true;
        break;
      }
    }

    if (userUpdated) {
      requests.push(baseApi.User.AddOrUpdate.bind(null, newUser));
    }

    // If user has group changes, add those requests as well
    // NOTE: newUser.Group is an array of Group OBJECTS
    // prevUser.Group is an array of Group IDs
    if (addedGroups.length > 0) {
      requests.push(baseApi.User.AddGroups.bind(null, newUser.userId, addedGroups));
    }

    if (removedGroups.length > 0) {
      requests.push(baseApi.User.RemoveGroups.bind(null, newUser.userId, removedGroups));
    }


    // If user has user survey type changes, add those requests as well
    // NOTE: newUser.checkInSurveyId is the survey id of the user survey type for the survey type of check in
    // prevUser.Group is an array of Group IDs

    if (removedUserSurveyTypes.length > 0 || addedUserSurveyTypes.length > 0) {
      if (addedUserSurveyTypes.length === 0 && removedUserSurveyTypes.length > 0) {

        requests.push(baseApi.User.RemoveUserSurveyTypes.bind(null, newUser.userId, removedUserSurveyTypes));
      };
      if (removedUserSurveyTypes.length === 0 && addedUserSurveyTypes.length > 0) {

        requests.push(baseApi.User.AddUserSurveyTypes.bind(null, newUser.userId, addedUserSurveyTypes));
      };

      if (removedUserSurveyTypes.length > 0 && addedUserSurveyTypes.length > 0) {
        requests.push(removeSurveyUserTypesFromUserThenAdd.bind(null, newUser.userId, removedUserSurveyTypes, addedUserSurveyTypes));
      }


    }

    return Promise.all(requests.map(request => request()))
      .then(axios.spread((...responses) => {
        // Return user, either updated or previous, depending on whether a putUser request was made
        if (userUpdated) {
          return responses[0].data;
        } else {
          return prevUser;
        }

      }));
  }

  function removeSurveyUserTypesFromUserThenAdd(userId, removedUserSurveyTypes, addedUserSurveyTypes) {
    return baseApi.User.RemoveUserSurveyTypes(userId, removedUserSurveyTypes)
      .then(result => {
        baseApi.User.AddUserSurveyTypes(userId, addedUserSurveyTypes);

        return result;
      });


  }

  function addNewUser(user) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi addNewUser'); }
    // extract groups from user
    const { groups, checkInSurveyId } = user;
    delete user.checkInSurveyId;



    // return putUser(user)
    return baseApi.User.AddOrUpdate(user)
      .then(result => {
        const createdUser = result.data;

        if (!groups || groups.length === 0) return createdUser;

        const userId = createdUser.userId;
        const groupIds =  groups.map(group => group.groupId);

        return baseApi.User.AddGroups(userId, groupIds)
          .then(result => {

            let surveyTypeSurvey = [{ surveyTypeId: 1, surveyId: checkInSurveyId }];
            if (checkInSurveyId != null) {
              return baseApi.User.AddUserSurveyTypes(userId, surveyTypeSurvey)
                .then(result => {
                  return createdUser;
                })
                .catch(error => {
                  baseApi.User.delete(createdUser)
                    .then(result => {
                      throw new Error('User not created')
                    })
                    .catch(error => {
                      throw new Error('User not created')
                    })
                })
            } else {
              return createdUser;
            }
          })
          .catch(error => {
            baseApi.User.delete(createdUser)
              .then(result => {                 
                throw new Error('User not created')
              })
              .catch(error => {
                throw new Error('User not created')
              })
          })
          
      })
      .catch(error => {
        throw new Error('User not created');
      });
  }

  function getStagedOrganizationCount() {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getStagedOrganizationCount'); }
    return axios.all([
      baseApi.Staging.Corporation.get(), 
      baseApi.Staging.Facility.get()
    ])
      .then(axios.spread((stagedCorporationResp, stagedFacilityResp) => {
        const stagedCorporations = stagedCorporationResp.data;
        const stagedFacilities = stagedFacilityResp.data;

        return stagedCorporations.length + stagedFacilities.length;
      }));
  }

  function getUnreadMessageCount() {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getUnreadMessageCount'); }
    return baseApi.UserMessage.GetUnreadThreadCountForCurrentUser()
      .then(result => result.data);
  }

  function getAllOrganizations() {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getAllOrganizations'); }
    return axios.all([
      baseApi.Staging.Corporation.get(), 
      baseApi.Staging.Facility.get(),
      baseApi.Corporation.GetAll(),
      baseApi.Facility.GetAll()])
        .then(axios.spread((stagedCorporationResp, stagedFacilityResp, mimiCorporationResp, mimiFacilityResp) => {
          const stagedCorporations = stagedCorporationResp.data;
          const stagedFacilities = stagedFacilityResp.data;
          const mimiCorporations = mimiCorporationResp.data;
          const mimiFacilities = mimiFacilityResp.data;

      // Order below is critical; mimi entities need to have their uiData
      // added before staged entities are processed
      // 1 GENERATE ARRAYS OF FACILITIES LINKED TO EACH CORPORATION
      let corporationsWithLinkedFacilities = {};

      stagedFacilities.forEach((facility) => {
        facility.parentSalesforceAccountIds.forEach((id) => {
          if (corporationsWithLinkedFacilities[id]) {
            corporationsWithLinkedFacilities[id].push(facility.salesforceAccountId);
          } else {
            corporationsWithLinkedFacilities[id] = [facility.salesforceAccountId];
          }
        });
      });

       mimiFacilities.forEach((facility) => {
        facility.corporationSalesforceAccountIds.forEach((idObject) => {
          const { salesforceAccountId : id } = idObject;

          if (corporationsWithLinkedFacilities[id]) {
            // add only if there's not already a staged facility with the same id
            if (!corporationsWithLinkedFacilities[id].some(existingId => existingId === facility.salesforceAccountId)) {
              corporationsWithLinkedFacilities[id].push(facility.salesforceAccountId);
            }
          } else {
            corporationsWithLinkedFacilities[id] = [facility.salesforceAccountId];
          }
        });        
      });

      // 2 ADD UI DATA TO MIMI FACILITIES
      mimiFacilities.forEach((facility) => {
        facility.uiData = {
          linkedCorporationIds: facility.corporationSalesforceAccountIds.map(idObject => idObject.salesforceAccountId)
        }
      });
      
      // 3 ADD UI DATA TO MIMI CORPORATIONS
      mimiCorporations.forEach((corporation) => {
        let linkedFacilityIds = corporationsWithLinkedFacilities[corporation.salesforceAccountId];

        corporation.uiData = {
          linkedFacilityIds: (linkedFacilityIds ? linkedFacilityIds : [])
        };
      })
      
      // 4 ADD UI DATA TO STAGED FACILITIES
      stagedFacilities.forEach((facility) => {
        facility.uiData = {
          type: 'facility',
          relatedMimiFacility: null,
          isNew: null,
          linkedCorporationIds: facility.parentSalesforceAccountIds,
          updatedFields: []
        };

        let relatedMimiFacility = mimiFacilities.find(mimiFacility => mimiFacility.salesforceAccountId === facility.salesforceAccountId);

        facility.uiData.relatedMimiFacility = relatedMimiFacility;
        facility.uiData.isNew = (relatedMimiFacility ? false : true); // TODO: Remove once UI makes use of uiData.status
        facility.uiData.status = (facility.status === 'rejected' ? 'rejected' : (relatedMimiFacility ? 'updated' : 'new'));  // TODO: Confirm that facility.Status is always either null or 'rejected'
        facility.uiData.updatedFields = (relatedMimiFacility ? getUpdatedFacilityFields(facility, relatedMimiFacility) : []);
      }); 
      
      // 5 ADD UI DATA TO STAGED CORPORATIONS
      stagedCorporations.forEach((corporation) => {
        let linkedFacilityIds = corporationsWithLinkedFacilities[corporation.salesforceAccountId];

        corporation.uiData = { 
          type: 'corporation',
          relatedMimiCorporation: null,
          isNew: null,
          linkedFacilityIds: (linkedFacilityIds ? linkedFacilityIds : []),
          updatedFields: []
        };

        let relatedMimiCorporation = mimiCorporations.find(mimiCorporation => mimiCorporation.salesforceAccountId === corporation.salesforceAccountId);

        corporation.uiData.relatedMimiCorporation = relatedMimiCorporation;
        corporation.uiData.isNew = (relatedMimiCorporation ? false : true); // TODO: Remove once UI makes use of uiData.status
        corporation.uiData.status = (corporation.status === 'rejected' ? 'rejected' : (relatedMimiCorporation ? 'updated' : 'new')); // TODO: Confirm that corporation.Status is always either null or 'rejected'
        corporation.uiData.updatedFields = (relatedMimiCorporation ? getUpdatedCorporationFields(corporation, relatedMimiCorporation) : []);
      }); 


      return {
        stagedOrganizations: stagedCorporations.concat(stagedFacilities),
        corporations: mimiCorporations,
        facilities: mimiFacilities
      }
    }));
  }

  function getMoodSnapshotData(selectedMonitoredIndividual, selectedUnit) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getMoodSnapshotData'); }
    const pastNumberOfDays = 7;

    let request;

    if (selectedMonitoredIndividual) {
      request = baseApi.CheckIn.GetMoodAveragesForMonitoredIndividual.bind(null, selectedMonitoredIndividual.monitoredIndividualId, pastNumberOfDays);
    } else if (selectedUnit) {
      request = baseApi.CheckIn.GetMoodAveragesForUnit.bind(null, selectedUnit.unitId, pastNumberOfDays);
    } else {
      request = baseApi.CheckIn.GetMoodAverages.bind(null, pastNumberOfDays);
    }

    return (
      request()
        .then(result => {
          const moodAverages = result.data;
          let dailyMoods = [];

          // Create collection of dates
          let today = new Date();
          today.setHours(0);
          today.setMinutes(0);
          today.setSeconds(0);
          today.setMilliseconds(0);

          for (let i = 0; i < pastNumberOfDays; i++) {
            dailyMoods.push({
              date: subDays(today, i),
              mood: null
            })
          }

          for (let date in moodAverages) {
            let parsedDate = parseISO(date);
            let dailyMood = dailyMoods.find(item => isSameDay(item.date, parsedDate));
            if (dailyMood) dailyMood.mood = moodAverages[date];
          }

          return dailyMoods;
        })
    );
  }

  function getLongestResponseData(days) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getLongestResponseData'); }
    const pastNumberOfDays = days;
    const numberOfAlarmers = 5;
    
    return baseApi.Alarm.GetTopAlarmsByResponseTime(numberOfAlarmers, pastNumberOfDays)
      .then(result => result.data);
  }

  function getMostAlarmsData(days) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getMostAlarmsData'); }
    const pastNumberOfDays = days;
    const numberOfAlarmers = 5;
    
    return baseApi.Alarm.GetTopAlarmers(numberOfAlarmers, pastNumberOfDays)
      .then(result => result.data);
  }

  function  setModuleGroupTypeAvailabilityKeys(module) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi setModuleGroupTypeAvailabilityKeys'); }
    module.isStatus = module.moduleGroupTypeTags.some(tag => tag.groupTypeId === 1);
    module.isCorporate = module.moduleGroupTypeTags.some(tag => tag.groupTypeId === 2);
    module.isFacility = module.moduleGroupTypeTags.some(tag => tag.groupTypeId === 3);
    module.isFamily = module.moduleGroupTypeTags.some(tag => tag.groupTypeId === 4);
    
    return module;
  }

  function getModulesByAdmin(admin, organization) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getModulesByAdmin'); }
    const { groupTypeString } = admin;

    switch (groupTypeString) {
      case 'Status Solutions':
        return baseApi.Module.GetAll()
          .then(result => {
            let modules = result.data;

            return modules.map(module => setModuleGroupTypeAvailabilityKeys(module));
          });
      case 'Corporate':
        const { corporationId } = admin; 
        const corporation = organization; 

        return axios.all([baseApi.Module.GetAllForGroupType(2), baseApi.Corporation.GetAllModules(corporationId)])
          .then(axios.spread((corporationModulesResponse, enabledModulesResponse) => {
            const corporationModules = corporationModulesResponse.data;
            const enabledModules = enabledModulesResponse.data;

            // set module Status key appropriately
            corporationModules.map(module => {
              if (!corporation.corporationFacilityTags.some(corporationFacility => facilityCanUtilizeModule(corporationFacility.facility, module))) {
                module.CorporationStatus = 'Not Available';
              } else {
                if (enabledModules.some(enabledModule => enabledModule.moduleId === module.moduleId)) {
                  module.corporationStatus = 'Enabled';
                } else {
                  module.corporationStatus = 'Disabled';
                }
              }

              return setModuleGroupTypeAvailabilityKeys(module);
            })

            return corporationModules;
          }));
      case 'Facility':
        const { facilityId } = admin;
        return axios.all([baseApi.Module.GetAllForGroupType(3), baseApi.Module.GetAllForGroupType(4), baseApi.Facility.GetAllModules(facilityId, 3), baseApi.Facility.GetAllModules(facilityId, 4), baseApi.Facility.Id(facilityId)])
          .then(axios.spread((facilityModulesResponse, familyModulesResponse, enabledFacilityModulesResponse, enabledFamilyModulesResponse, facilityResponse) => {
            // Get all modules for group types Family/Facility
            const allFacilityModules = facilityModulesResponse.data;
            const allFamilyModules = familyModulesResponse.data;
            const enabledFacilityModules = enabledFacilityModulesResponse.data;
            const enabledFamilyModules = enabledFamilyModulesResponse.data;
            const facility = facilityResponse.data;

            // Concatenate family and facility modules and remove dupes
            const allModules = [...allFamilyModules];
            allFacilityModules.forEach((facilityModule) => {
              if(!allModules.some(module => module.moduleId === facilityModule.moduleId)) {
                allModules.push(facilityModule);
              }
            });

            // Set module Status key appropriately 
            allModules.map(module => {
              if (!allFacilityModules.some(facilityModule => facilityModule.moduleId === module.moduleId)) {
                module.facilityStatus = 'None';
              } else if (!facilityCanUtilizeModule(facility, module)) {
                module.facilityStatus = 'Not Available';
              } else  {
                if (enabledFacilityModules.some(enabledModule => enabledModule.moduleId === module.moduleId)) {
                  module.facilityStatus = 'Enabled';
                } else {
                  module.facilityStatus = 'Disabled';
                }
              }

              if (!allFamilyModules.some(familyModule => familyModule.moduleId === module.moduleId)) {
                module.familyStatus = 'None';
              } else if (!facilityCanUtilizeModule(facility, module)) {
                module.familyStatus = 'Not Available';
              } else  {
                if (enabledFamilyModules.some(enabledModule => enabledModule.moduleId === module.moduleId)) {
                  module.familyStatus = 'Enabled';
                } else {
                  module.familyStatus = 'Disabled';
                }
              }

              return setModuleGroupTypeAvailabilityKeys(module);
            });

            return allModules;
          }));

      default:
        return [];
    }
  }  
  
  // TODO: If user is a family user linked to multiple
  // MonitoredIndividuals, this just uses the id of the
  // first.  If there are no linked MonitoredIndividuals,
  // it just uses 1.  This is not correct long-term.
  function getWeather(user, selectedMonitoredIndividual) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getWeather'); }
    let monitoredIndividualId; 

    if (user.groupTypeString === 'Family') {
      if (selectedMonitoredIndividual) {
        monitoredIndividualId = selectedMonitoredIndividual.monitoredIndividualId;
      } else {
        return Promise.reject(new Error('No selected MonitoredIndividual found. Cannot fetch weather for Family user without a selected MonitoredIndividual'));
      }
    }
    
    if (monitoredIndividualId) {
      return baseApi.Weather.GetCurrent(monitoredIndividualId)
        .then(result => result.data);
    } else {
      return baseApi.Weather.GetCurrent()
        .then(result => result.data);
    }
  }  

  function getNextEvent(user, selectedMonitoredIndividual) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getNextEvent'); }
    // If family user, get next event for 1st MI; 
    // TODO: Change this to the selected MI once we have Environment Context set up
    if (user.groupTypeString === 'Family') {
      const { monitoredIndividualId } = selectedMonitoredIndividual;

      if (!monitoredIndividualId) return Promise.reject(new Error('No MonitoredIndividualSelected'));
      
      return baseApi.SocialEvent.GetNextByMonitoredIndividual(monitoredIndividualId).then(result => result.data);
    } else if (user.groupTypeString === 'Facility') {

      // If facility user and there's a selectedMI, get next event for selectedMI
      if (selectedMonitoredIndividual) {
        const { monitoredIndividualId } = selectedMonitoredIndividual;

        if (!monitoredIndividualId) return Promise.reject(new Error('No MonitoredIndividual selected'));

        return baseApi.SocialEvent.GetNextByMonitoredIndividual(monitoredIndividualId).then(result => result.data);
      } else {
        // If facility user and there's NO selectedMI, get next event for facility
        return baseApi.SocialEvent.GetNextByFacility(user.facilityId).then(result => result.data);
      }
    } else {
      return Promise.reject(new Error(`User Group Type "${user.groupTypeString}" cannot view Next Event`));
    }
  }

  function getEventCalendarData(user, selectedMonitoredIndividual, date) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getEventCalendarData'); }
    // If family user, get events for month for 1st MI;
    if (user.groupTypeString === 'Family') {
      const { monitoredIndividualId } = selectedMonitoredIndividual;

      if (!monitoredIndividualId) return Promise.reject(new Error('No MonitoredIndividual selected'));
      
      return baseApi.SocialEvent.GetAllForMonthByMonitoredIndividual(monitoredIndividualId, date).then(result => result.data);
    } else if (user.groupTypeString === 'Facility') {
      // If facility user and there's a selectedMI, get month's events for selectedMI
      if (selectedMonitoredIndividual) {
        const { monitoredIndividualId } = selectedMonitoredIndividual;

        if (!monitoredIndividualId) return Promise.reject(new Error('No MonitoredIndividual selected'));

        return baseApi.SocialEvent.GetAllForMonthByMonitoredIndividual(monitoredIndividualId, date).then(result => result.data);
      } else {    
        // If facility user and there's NO selectedMI, return empty array
        return Promise.resolve([]);
      }
    } else {
      return Promise.reject(new Error(`User Group Type "${user.groupTypeString}" cannot view Event Calendar data`));
    }
  }

  function getCalendarDetailsData(user, selectedMonitoredIndividual, date) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getCalendarDetailsData'); }
    // Get Facility ID off User OR MonitoredIndividual
    let facilityId;

    if (user.groupTypeString === 'Facility') facilityId = user.facilityId;
    // TODO: In future, get this facilityId from selected monitored individual
    if (user.groupTypeString === 'Family') facilityId = selectedMonitoredIndividual.lastKnownFacilityId
    if (!facilityId) return Promise.reject(new Error('No FacilityId'));

    if (user.groupTypeString !== 'Family' && !selectedMonitoredIndividual) {
      return baseApi.SocialEvent.GetAllForMonthByFacility(facilityId, date)
        .then(result => {
          return {
            facilityEvents: result.data,
            monitoredIndividualEvents: []
          }
        })
    } else {
      let monitoredIndividualId;
      if (user.groupTypeString === 'Facility') monitoredIndividualId = selectedMonitoredIndividual.monitoredIndividualId;
      if (user.groupTypeString === 'Family') monitoredIndividualId = user.monitoredIndividualUsers[0].monitoredIndividualId;   
      if (!monitoredIndividualId) return Promise.reject(new Error('No MonitoredIndividualId'));

      return axios.all([
        baseApi.SocialEvent.GetAllForMonthByFacility(facilityId, date),
        baseApi.SocialEvent.GetAllForMonthByMonitoredIndividual(monitoredIndividualId, date)
      ])
        .then(axios.spread((facilityEventsResp, monitoredIndividualEventsResponse) => {
          return {
            facilityEvents: facilityEventsResp.data,
            monitoredIndividualEvents: monitoredIndividualEventsResponse.data
          }
        }));
    }
  }

  function getGroupsByAdmin(user) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getGroupsByAdmin'); }
    const { groupTypeString } = user;

    switch (groupTypeString) {
      case 'Status Solutions':
        return baseApi.StatusSolutions.GetAllGroups(user.statusSolutionsId)
          .then(result =>  {
            if (result.status === 204) return [];
  
            return result.data;
          })
          .catch(error => { throw new Error('Unable to get groups for Status Solutions')});
      case 'Corporate':
        return baseApi.Corporation.GetAllGroups(user.corporationId)
          .then(result =>  {
            if (result.status === 204) return [];
  
            return result.data;
          })
          .catch(error => { throw new Error('Unable to get groups for current corporation')});
      case 'Facility':
        return baseApi.Facility.GetAllGroups(user.facilityId)
          .then(result =>  {
            if (result.status === 204) return [];
  
            return result.data;
          })
          .catch(error => { throw new Error('Unable to get groups for current facility')});
      case 'Family':
        return Promise.resolve([]);
      default: 
        return Promise.reject(new Error ('User Group Type not recognized'));
    }
  }

  function getNotesHistory(noteType, monitoredIndividual, unit, numberToSkip, numberToTake, order, searchTerms) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getNotesHistory'); }
    const monitoredIndividualId = monitoredIndividual ? monitoredIndividual.monitoredIndividualId : null;
    const unitId = unit ? unit.unitId : null;

    if ((unitId === null && monitoredIndividualId === null) ||
        (unitId !== null && monitoredIndividualId !== null)) {
          return Promise.reject(new Error('Must pass exactly one of monitoredIndividual or unit'));
        }
    
    switch(noteType) {
      case 'all':
        return baseApi.StaffCheckHistoryNote.GetPagedNotes(monitoredIndividualId, unitId, numberToSkip, numberToTake, order, searchTerms)
          .then(result => result.data);
      case 'family':
        return baseApi.StaffCheckHistoryNote.GetPagedFamilyNotes(monitoredIndividualId, unitId, numberToSkip, numberToTake, order, searchTerms)
          .then(result => result.data);
      case 'staff':
          return baseApi.StaffCheckHistoryNote.GetPagedStaffNotes(monitoredIndividualId, unitId, numberToSkip, numberToTake, order, searchTerms)
          .then(result => result.data);
      default:
        return Promise.reject(new Error('noteType not provided'));
    }
  }
  
  function getAverageResponseData(days, user, selectedMonitoredIndividual, selectedUnit) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getAverageResponseData'); }
    switch(user.groupTypeString) {
      case 'Status Solutions':
      case 'Corporate':
        return baseApi.Alarm.GetAlarmAverage(days).then(result => result.data);
      case 'Facility':
        if (selectedMonitoredIndividual) { 
          return baseApi.Alarm.GetAlarmAverage(days, selectedMonitoredIndividual.monitoredIndividualId).then(result => result.data);
        } else if (selectedUnit) {
          return baseApi.Alarm.GetAlarmAverage(days, null, selectedUnit.unitId).then(result => result.data);
        } else {
          return baseApi.Alarm.GetAlarmAverage(days).then(result => result.data);
        }
      case 'Family': 
        return baseApi.Alarm.GetAlarmAverage(days, selectedMonitoredIndividual.monitoredIndividualId).then(result => result.data);
      default: 
        return Promise.reject(new Error('User GroupTypeString not recognized'));
    }
  }

  function getSurveyResponses(user, numberToSkip, numberToTake, order, searchTerms) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getSurveysWithDistributions'); }
    return baseApi.Survey.GetSurveyResponsesForCurrentUser(numberToSkip, numberToTake, order, searchTerms)
      .then(result => {

        // 1 Get survey responses from results
        let surveyReponsesCollections = result.data;

        // 2 Sort each group of distributions in surveyWithDistributionsCollections
        surveyReponsesCollections.forEach((collection) => {
          collection.survey.surveyDistributions.sort((a, b) => {

            return isBefore(parseISO(a.sendOn), parseISO(b.sendOn)) ? 1 : -1
          });
        })

        // 3 Sort the surveyWithDistributionsCollections by survey name
        surveyReponsesCollections.sort((a, b) => {
          return a.survey.surveyName > b.survey.surveyName ? 1 : -1;
        });

        return surveyReponsesCollections;

      });

  }
  
  function getMessageThreads(user) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getMessageThreads'); }
    return baseApi.UserMessage.GetThreadsForCurrentUser()
      .then(result => {
        if (user.groupTypeString === 'Facility') {
          const threads = result.data;

          // 1 Group message threads by MonitoredIndividual
          let monitoredIndividualThreadCollections = []

          threads.forEach((thread) => {
            let monitoredIndividualThreadCollection = monitoredIndividualThreadCollections.find(collection => collection.monitoredIndividual.monitoredIndividualId === thread.monitoredIndividualId);

            if (monitoredIndividualThreadCollection) {
              monitoredIndividualThreadCollection.threads.push(thread);
            } else {
              monitoredIndividualThreadCollections.push({
                monitoredIndividual: thread.monitoredIndividual,
                threads: [thread]
              })
            }
          });

          // 2 Sort each group of threads in monitoredIndividualThreadCollections
          monitoredIndividualThreadCollections.forEach((collection) => {
            collection.threads.sort((a, b) => {
              if (!a.isReadByFacility && b.isReadByFacility) return -1;
              if (!b.isReadByFacility && a.isReadByFacility) return 1;
              
              return isBefore(parseISO(a.lastActive), parseISO(b.lastActive)) ? 1 : -1
            });
          })

          // 3 Sort the monitoredIndividualThreadCollections by whether they have any unread threads, then by time
          monitoredIndividualThreadCollections.sort((a, b) => {
            if (!a.threads[0].isReadByFacility && b.threads[0].isReadByFacility) return -1;
            if (!b.threads[0].isReadByFacility && a.threads[0].isReadByFacility) return 1;

            return isBefore(a.threads[0].lastActive, b.threads[0].lastActive) ? 1 : -1;
          });

          return monitoredIndividualThreadCollections;
          
        } else if (user.groupTypeString === 'Family') {
          const threads = result.data;
          
          threads.sort((a, b) => {
            if (!a.isReadByFamily && b.isReadByFamily) return -1;
            if (!b.isReadByFamily && a.isReadByFamily) return 1;
            
            return isBefore(a.lastActive, b.lastActive) ? 1 : -1
          });
          
          return threads;
        } 
      });
  }

  function getMessageThread(user, thread) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getMessageThread'); }
      return baseApi.UserMessage.id(thread.userMessageThreadId).then(result => {
        return result.data;
      });
  }

  function createNewMessageThread(user, monitoredIndividual, subject, message, isPrivate) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi createNewMessageThread'); }
    if (!user) return Promise.reject(new Error('No user provided'));
    if (!monitoredIndividual) return Promise.reject(new Error('No monitoredIndividual provided'));
    if (!subject) return Promise.reject(new Error('No subject provided'));
    if (!message) return Promise.reject(new Error('No message provided'));

    const userMessage = {
      sentOn: new Date(),
      userId: user.userId,
      message: message,
      userMessageThread: {
        subject: subject,
        isPrivate: isPrivate,
        monitoredIndividualId: monitoredIndividual.monitoredIndividualId,
        facilityId: monitoredIndividual.lastKnownFacilityId
      }
    }
    
    baseApi.UserMessage.Create(userMessage)
      .then(result => result.data);
  }

  function addToMessageThread(user, message, messageThreadId) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi addToMessageThread'); }
    if (!user) return Promise.reject(new Error('No user provided'));
    if (!message) return Promise.reject(new Error('No message provided'));
    if (!messageThreadId) return Promise.reject(new Error('No messageThreadId provided'));

    const userMessage = {
      sentOn: new Date(),
      userId: user.userId,
      message: message,
      userMessageThreadId: messageThreadId
    }

    baseApi.UserMessage.Create(userMessage)
      .then(result => result.data);
  }

  function getDeviceDetailsByUser(user, statusFilters, skip, take, sort, searchTerms) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getDeviceDetailsByUser'); }
    switch (user.groupTypeString) {
    case 'Status Solutions':
      return baseApi.AlarmDevice.GetAll(statusFilters, skip, take, sort, searchTerms).then(result => result.data);
    case 'Corporate':
      return baseApi.AlarmDevice.GetByCorporation(user.corporationId, statusFilters, skip, take, sort, searchTerms).then(result => result.data);
    case 'Facility':
      return baseApi.AlarmDevice.GetByFacility(user.facilityId, statusFilters, skip, take, sort, searchTerms).then(result => result.data);
    default:
      return Promise.reject(new Error('User does not belong to a GroupType permitted to view device details'));
    }
  }

  function getFacilityMonitoredIndividualsAndUnits(facility) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getFacilityMonitoredIndividualsAndUnits'); }
    const { facilityId } = facility;

    if (!facilityId) return Promise.reject(new Error('No FacilityId provided'));

    return axios.all([
      baseApi.Facility.GetAllMonitoredIndividuals(facilityId),
      baseApi.Facility.GetUnits()
    ])
      .then(axios.spread((monitoredIndividualsResp, unitsResp) => {
        return {
          monitoredIndividuals: monitoredIndividualsResp.data,
          units: unitsResp.data
        };
      }));
  }

  function getLatestCheckin(unitOrMonitoredIndividual) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getLatestCheckin'); }
    const { monitoredIndividualId, unitId } = unitOrMonitoredIndividual;

    if (monitoredIndividualId) {
      return baseApi.CheckIn.GetLatestByMonitoredIndividual(monitoredIndividualId).then(result => result.data);     
    } else if (unitId) {
      return baseApi.CheckIn.GetLatestByUnit(unitId).then(result => result.data);
    } else {
      return Promise.reject(new Error('Cannot fetch latest check-in without UnitId or MonitoredIndividualId'));
    }
  }

  function rejectStagedOrganization(organization, rejectReason) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi rejectStagedOrganization'); }
    const { type } = organization.uiData;

    let orgToReject = {...organization};
    orgToReject.status = 'rejected';
    delete orgToReject.uiData; // this is necessary to avoid a circular structure

    if (type === 'corporation') {
      return baseApi.Staging.Corporation.put(orgToReject, rejectReason)
        .then(result => result.data)
    } else if (type === 'facility') {
      return baseApi.Staging.Facility.put(orgToReject, rejectReason)
        .then(result => result.data)
    }
  }

  function searchFacilityUsers(searchTerms) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi searchFacilityUsers'); }
    const queryObject = {
      SearchValues: searchTerms,
      SortDesc: false,
      NumberToSkip: 0,
      NumberToTake: 1000,
      FilterBy: [
        { GroupTypeEnum: 'Facility' }
      ]
    }

    return baseApi.User.GetUsersForCurrentUser(queryObject)
      .then(result => result.data);
  }

  function searchCorporationUsers(searchTerms) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi searchCorporationUsers'); }
      const queryObject = {
          SearchValues: searchTerms,
          SortDesc: false,
          NumberToSkip: 0,
          NumberToTake: 1000,
          FilterBy: [
              { GroupTypeEnum: 'Corporate' }
          ]
      }

      return baseApi.User.GetUsersForCurrentUser(queryObject)
          .then(result => result.data);
  }

  function searchStatusSolutionsUsers(searchTerms) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi searchStatusSolutionsUsers'); }
      const queryObject = {
          SearchValues: searchTerms,
          SortDesc: false,
          NumberToSkip: 0,
          NumberToTake: 1000,
          FilterBy: [
              { GroupTypeEnum: 'StatusSolutions' }
          ]
      }

      return baseApi.User.GetUsersForCurrentUser(queryObject)
          .then(result => result.data);
  }

  function uploadLogo(organization, logo) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi uploadLogo'); }
    let formData = new FormData();

    formData.append('file', logo);

    if (organization.corporationId) {
      return baseApi.Corporation.Logo.Id.put(organization.corporationId, formData).then(result => result.data);
    } else if (organization.facilityId) {
      return baseApi.Facility.Logo.Id.put(organization.facilityId, formData).then(result => result.data);
    } else {
      return Promise.reject(new Error('Organization type not found'));
    }
  }

  function createGroup(group) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi createGroup'); }
    let createdGroup;
    let { groupMembers, checkInSurveyId } = group;

    delete group.groupMembers;
    delete group.checkInSurveyId;

    return baseApi.Group.AddOrUpdate(group)
      .then(result => {
        // Cache new group id for use in delete call if adding users fails
        createdGroup = result.data;

        if (!groupMembers || groupMembers.length === 0) return createdGroup 

        const membersToAdd = { groupId: createdGroup.groupId, userIds: groupMembers.map(member => member.userId) };            
        
        // Add users to new group
        return baseApi.Group.AddUsers(membersToAdd)
          .then(result => {

            let surveyTypeSurvey = [{ surveyTypeId: 1, surveyId: checkInSurveyId }];

            if (checkInSurveyId == undefined || checkInSurveyId === 0) {
              return createdGroup;
            }

            return baseApi.Group.AddGroupSurveyTypes(createdGroup.groupId, surveyTypeSurvey)
              .then(result => {
                return createdGroup;
              })
              .catch(error => {
                baseApi.Group.DeleteGroup(createdGroup.groupId)
                  .then(result => {
                    throw new Error('Group not created')
                  })
                  .catch(error => {
                    throw new Error('Group not created')
                  })
              })


          })
          .catch(error => {
            // If users not successfully added, delete group
            baseApi.Group.DeleteGroup(createdGroup.groupId)
              .then(result => {
                throw new Error('Group not created');
              })
          })
      })
  }

  function getGroupMembers(group) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getGroupMembers'); }
    if (!group) return [];
    const { groupId } = group;

    return baseApi.Group.GetUsers(groupId)
      .then(result => {
        // handle empty group
        if (result.status === 204) {
          return [];
        } else {
          return result.data;
        }
      })
  }  
  
  function updateGroup(updatedGroup, originalGroup) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi updateGroup'); }
    let originalMembers = originalGroup.groupMembers;
    let updatedMembers = updatedGroup.groupMembers; 
    let allRequests = [];
    let groupUpdated = false;
    let addedGroupSurveyTypes = []
    let removedGroupSurveyTypes = [];

    if (updatedGroup.checkInSurveyId && !originalGroup.checkInSurveyId && updatedGroup.checkInSurveyId !== "" && updatedGroup.checkInSurveyId !== 0) {
      let surveyTypeSurveyAdd = { surveyTypeId: 1, surveyId: updatedGroup.checkInSurveyId };
      addedGroupSurveyTypes.push(surveyTypeSurveyAdd);
    }



    if (!updatedGroup.checkInSurveyId && originalGroup.checkInSurveyId) {
      let surveyTypeSurveyRemove = { surveyTypeId: 1, surveyId: originalGroup.checkInSurveyId };
      removedGroupSurveyTypes.push(surveyTypeSurveyRemove);
    }


    if (updatedGroup.checkInSurveyId && originalGroup.checkInSurveyId && updatedGroup.checkInSurveyId !== originalGroup.checkInSurveyId && updatedGroup.checkInSurveyId !== "") {
      let surveyTypeSurveyAdd = { surveyTypeId: 1, surveyId: updatedGroup.checkInSurveyId };
      let surveyTypeSurveyRemove = { surveyTypeId: 1, surveyId: originalGroup.checkInSurveyId };
      addedGroupSurveyTypes.push(surveyTypeSurveyAdd);
      removedGroupSurveyTypes.push(surveyTypeSurveyRemove);
    }



    // filter members to add / remove      
    const membersToRemove = originalMembers
        .filter(originalMember => !updatedMembers.some(updatedMember => updatedMember.userId === originalMember.userId))
        .map(member => member.userId);
    const membersToAdd = updatedMembers
        .filter(updatedMember => !originalMembers.some(originalMember => originalMember.userId === updatedMember.userId))
        .map(member => member.userId);

    if (updatedGroup.name !== originalGroup.name ||
      updatedGroup.canCorrespond !== originalGroup.canCorrespond ||
      updatedGroup.canImpersonate !== originalGroup.canImpersonate ||
      updatedGroup.canViewSurveyResponses !== originalGroup.canViewSurveyResponses) {
          groupUpdated = true;
          allRequests.push(baseApi.Group.AddOrUpdate.bind(null, updatedGroup));
    }

    if (membersToRemove.length > 0) allRequests.push(baseApi.Group.RemoveUsers.bind(null, { groupId: updatedGroup.groupId, userIds: membersToRemove }));
    if (membersToAdd.length > 0) allRequests.push(baseApi.Group.AddUsers.bind(null, { groupId: updatedGroup.groupId, userIds: membersToAdd }));


    // If user has user survey type changes, add those requests as well
    // NOTE: newUser.checkInSurveyId is the survey id of the user survey type for the survey type of check in
    // prevUser.Group is an array of Group IDs

    if (removedGroupSurveyTypes.length > 0 || addedGroupSurveyTypes.length > 0) {
      if (addedGroupSurveyTypes.length === 0 && removedGroupSurveyTypes.length > 0) {

        allRequests.push(baseApi.Group.RemoveGroupSurveyTypes.bind(null, updatedGroup.groupId, removedGroupSurveyTypes));
      };
      if (removedGroupSurveyTypes.length === 0 && addedGroupSurveyTypes.length > 0) {

        allRequests.push(baseApi.Group.AddGroupSurveyTypes.bind(null, updatedGroup.groupId, addedGroupSurveyTypes));
      };

      if (removedGroupSurveyTypes.length > 0 && addedGroupSurveyTypes.length > 0) {
        allRequests.push(removeSurveyUserTypesFromGroupThenAdd.bind(null, updatedGroup.groupId, removedGroupSurveyTypes, addedGroupSurveyTypes));
      }


    }

    if (allRequests.length === 0) return new Promise.resolve(originalGroup);

    return axios.all(allRequests.map(request => request()))
      .then(axios.spread((...responses) => {
        if (groupUpdated) {
          return responses[0].data;
        } else {
          return originalGroup;
        }
      }))
      .catch(error => {
        throw new Error('Unable to update group');
      })      
  }
  function removeSurveyUserTypesFromGroupThenAdd(groupId, removedGroupSurveyTypes, addedGroupSurveyTypes) {
    return baseApi.Group.RemoveGroupSurveyTypes(groupId, removedGroupSurveyTypes)
      .then(result => {
        baseApi.Group.AddGroupSurveyTypes(groupId, addedGroupSurveyTypes);

        return result;
      });


  }

  function updateModuleForFacility(facility, originalModule, updatedModule, staffCheckNoteSettings) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi updateModuleForFacility'); }
    const { facilityId } = facility;
    let facilityRequests = [];
    let moduleBeingDisabled = false;
    let moduleBeingEnabled = false;


    // Facility permission requests
    let groupTypesToRemove = [];
    let groupTypesToAdd = [];
    if (originalModule.familyStatus === 'Enabled' && updatedModule.familyStatus === 'Disabled') {
      groupTypesToRemove.push(4);
    } else if (originalModule.familyStatus === 'Disabled' && updatedModule.familyStatus === 'Enabled') {
      groupTypesToAdd.push(4);
    }

    if (originalModule.facilityStatus === 'Enabled' && updatedModule.facilityStatus === 'Disabled') {
      groupTypesToRemove.push(3);
    } else if (originalModule.facilityStatus === 'Disabled' && updatedModule.facilityStatus === 'Enabled') {
      groupTypesToAdd.push(3);
    }

    if (groupTypesToAdd.length > 0) {
      facilityRequests.push(baseApi.Facility.AddModule.bind(null, facilityId, originalModule.moduleId, groupTypesToAdd));
    } 

    if (groupTypesToRemove.length > 0) {
      facilityRequests.push(baseApi.Facility.RemoveModule.bind(null, facilityId, originalModule.moduleId, groupTypesToRemove));
    }

    // Other facility requests (Staff Check Notes)
    if (staffCheckNoteSettings.familyNoteRequired !== null || staffCheckNoteSettings.staffNoteRequired !== null) {

      const isStaffNoteRequired = 
        staffCheckNoteSettings.staffNoteRequired !== null ? 
        staffCheckNoteSettings.staffNoteRequired :
        facility.isStaffCheckHistoryStaffNoteRequired
      
      const isFamilyNoteRequired = 
        staffCheckNoteSettings.familyNoteRequired !== null ? 
        staffCheckNoteSettings.familyNoteRequired :
        facility.isStaffCheckHistoryFamilyNoteRequired
        facilityRequests.push(baseApi.Facility.SetCheckInNotesRequirements.bind(null, facilityId, isStaffNoteRequired, isFamilyNoteRequired));
    }  

    // Group requests      
    let groupsWereUpdated = false;
    let groupsWereAdded = false;
    let groupsWereRemoved = false;
    let updatedGroups = null;

    // If module is being disabled, remove all group permissions
    if (moduleBeingDisabled) {
      groupsWereUpdated = true;
      updatedGroups = [];
    } else {
      groupsWereAdded = updatedModule.groupPermissions.some(updatedGroup => {
        return !originalModule.groupPermissions.some(originalGroup => updatedGroup === originalGroup)
      });

      groupsWereRemoved = originalModule.groupPermissions.some(originalGroup => {
        return !updatedModule.groupPermissions.some(updatedGroup => updatedGroup === originalGroup);
      });        

      groupsWereUpdated = groupsWereAdded || groupsWereRemoved;

      updatedGroups = updatedModule.groupPermissions;
    }
    
    if (facilityRequests.length === 0 && !groupsWereUpdated) return Promise.resolve('No update necessary');

    let groupRequest = baseApi.Module.SetGroups.bind(null, originalModule.moduleId, updatedGroups);
    
    // If module is being disabled for facility, do group removal first
    if (moduleBeingDisabled) {
      return groupRequest()
        .then(response => {
          // now make facility requests
          return axios.all(facilityRequests.map(request => request()))
          .then(axios.spread((...facilityResponses) => {
            return facilityResponses; 
          }));
        });
    } else if (moduleBeingEnabled) {
      // If module is being enabled for facility, do facility requests first 
      return axios.all(facilityRequests.map(request => request()))
      .then(axios.spread((...facilityResponses) => {

        // now make group requests
        return groupRequest()
          .then(response => {
            return response.data;
          })
      }));
    } else {
      // Otherwise, just make all requests together
      const allRequests = facilityRequests.concat(groupRequest);

      return axios.all(allRequests.map(request => request()))
        .then(axios.spread((...responses) => {
          return responses;
        }));
    }
  } 
  
  function getModuleGroupPermissions(module) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi getModuleGroupPermissions'); }
    return baseApi.Module.GetGroups(module.moduleId)
      .then(result => result.data.groupModuleTags)
  }

  function updateModuleForCorporation(corporationId, originalModule, updatedModule) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi updateModuleForCorporation'); }
    let corporationRequests = [];
    let moduleBeingDisabled = false;
    let moduleBeingEnabled = false;


    if (originalModule.corporationStatus === 'Enabled' && updatedModule.corporationStatus === 'Disabled') {
        corporationRequests.push(baseApi.Corporation.RemoveModule.bind(null, corporationId, originalModule.moduleId));
    }
    if (originalModule.corporationStatus === 'Disabled' && updatedModule.corporationStatus === 'Enabled') {
        corporationRequests.push(baseApi.Corporation.AddModule.bind(null, corporationId, originalModule.moduleId));
    }
    // Group requests      
    let groupsWereUpdated = false;
    let groupsWereAdded = false;
    let groupsWereRemoved = false;
    let updatedGroups = null;

    // If module is being disabled, remove all group permissions
    if (moduleBeingDisabled) {
        groupsWereUpdated = true;
        updatedGroups = [];
    } else {
        groupsWereAdded = updatedModule.groupPermissions.some(updatedGroup => {
            return !originalModule.groupPermissions.some(originalGroup => updatedGroup === originalGroup)
        });

        groupsWereRemoved = originalModule.groupPermissions.some(originalGroup => {
            return !updatedModule.groupPermissions.some(updatedGroup => updatedGroup === originalGroup);
        });

        groupsWereUpdated = groupsWereAdded || groupsWereRemoved;

        updatedGroups = updatedModule.groupPermissions;
    }

    if (corporationRequests.length === 0 && !groupsWereUpdated) return Promise.resolve('No update necessary');

    let groupRequest = baseApi.Module.SetGroups.bind(null, originalModule.moduleId, updatedGroups);

    // If module is being disabled for facility, do group removal first
    if (moduleBeingDisabled) {
        return groupRequest()
            .then(response => {
                // now make corporation requests
                return axios.all(corporationRequests.map(request => request()))
                    .then(axios.spread((...facilityResponses) => {
                        return facilityResponses;
                    }));
            });
    } else if (moduleBeingEnabled) {
        // If module is being enabled for corporation, do corporation requests first
        return axios.all(corporationRequests.map(request => request()))
            .then(axios.spread((...facilityResponses) => {

                // now make group requests
                return groupRequest()
                    .then(response => {
                        return response.data;
                    })
            }));
    } else {
        // Otherwise, just make all requests together
        const allRequests = corporationRequests.concat(groupRequest);

        return axios.all(allRequests.map(request => request()))
            .then(axios.spread((...responses) => {
                return responses;
            }));
    }
  } 

  function updateModuleGroupTypeAndGroupPermissions(module, prevPermissions, newPermissions, prevGroupPermissions, newGroupPermissions) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi updateModuleGroupTypeAndGroupPermissions'); }
    const { moduleId } = module;

    let addedGroupTypes = newPermissions.filter(groupType => !prevPermissions.some(prevGroupType => groupType === prevGroupType));
    let removedGroupTypes = prevPermissions.filter(groupType => !newPermissions.some(newGroupType => groupType === newGroupType));

    let addRequests = addedGroupTypes.map(groupType => baseApi.Module.AddToGroupType.bind(null, moduleId, groupType));
    let removeRequests = removedGroupTypes.map(groupType => baseApi.Module.RemoveFromGroupType.bind(null, moduleId, groupType));


    // Group requests      
    let groupsWereUpdated = false;
    let groupsWereAdded = false;
    let groupsWereRemoved = false;
    let updatedGroups = null;

    // If module is being disabled, remove all group permissions

    groupsWereAdded = newGroupPermissions.some(updatedGroup => {
        return !prevGroupPermissions.some(originalGroup => updatedGroup === originalGroup)
    });

    groupsWereRemoved = prevGroupPermissions.some(originalGroup => {
        return !newGroupPermissions.some(updatedGroup => updatedGroup === originalGroup);
    });

    groupsWereUpdated = groupsWereAdded || groupsWereRemoved;

    updatedGroups = newGroupPermissions;



  let groupRequest = baseApi.Module.SetGroups.bind(null, module.moduleId, updatedGroups);



  let requests = addRequests.concat(removeRequests).concat(groupRequest);

  if (requests.length === 0 && !groupsWereUpdated) return Promise.resolve('No update necessary');

  return axios.all(requests.map(request => request()))
  .then(axios.spread((...responses) => {
    return 'success'; // not sure what actually to return in this situation

  }))
  .catch(error => {
    throw new Error('group type and group permissions not correctly updated');
  })
  }  

  function addMimiOrganization(organization) {
    if (logComponent('convenienceApi')) { console.debug('convenienceApi addMimiOrganization'); }
    const orgToSubmit = { ...organization };
    delete orgToSubmit.uiData;

    if (orgToSubmit.stagedFacilityId) {
      return baseApi.Facility.AddOrUpdate(orgToSubmit)
    } else if (orgToSubmit.stagedCorporationId) {
      return baseApi.Corporation.AddOrUpdate(orgToSubmit)
    } else {
      return Promise.reject('Organization lacks CorporationId and FacilityId');
    }
  }

  return {
    getSecuredData,
    getOrganizationByUser,
    getFacilityMonitoredIndividuals,
    getWeblinksForOrganization,
    initializeUserUpdateModal,
    getMonitoredIndividualUsersFromMultipleFacilities,
    addNewUser,
    updateUser,
    getAllOrganizations,
    getMoodSnapshotData,
    getMostAlarmsData,
    getLongestResponseData,
    getModulesByAdmin,
    getNextEvent,
    getWeather,
    getEventCalendarData,
    getCalendarDetailsData,
    getGroupsByAdmin,
    getNotesHistory,
    getAverageResponseData,
    getSurveyResponses,
    getMessageThreads,
    getMessageThread,
    getDeviceDetailsByUser,
    getFacilityMonitoredIndividualsAndUnits,
    getLatestCheckin,
    getStagedOrganizationCount,
    getUnreadMessageCount,
    rejectStagedOrganization,
    uploadLogo,
    createGroup,
    getGroupMembers,
    updateGroup,
    getModuleGroupPermissions,
    updateModuleForFacility,
    updateModuleForCorporation,
    updateModuleGroupTypeAndGroupPermissions,
    addMimiOrganization,
    searchFacilityUsers,
    searchCorporationUsers,
    searchStatusSolutionsUsers,
    getFamilyRelationships
  }
})();

export default convenienceApi;